/**
 * Flash-blinding. A bright flash — an adjacent muzzle flash, NV goggles
 * overwhelmed by bright light, or (future) a flashbang throwable — dazes an
 * entity: for `CONFIG.blind.stunSec` it can't move at all, and for
 * `CONFIG.blind.durationSec` its vision is whited out (it perceives its whole
 * cone as overexposed, unable to make anything out), eroding back to normal as
 * the timer runs down. Applies to the player and every NPC (guards, the janitor)
 * alike.
 *
 * The sim reads these to gate movement (`isBlindStunned`) and perception
 * (`isBlinded` → an NPC can't spot the player); the renderer reads `blindTimer`
 * / `blindIntensity` for the player's screen whiteout. Headless — no PixiJS.
 *
 * FUTURE (throwables): a thrown flashbang would call `blindBurst` at its
 * detonation tile with its own (larger) radius. Smoke/EMP/frag/Molotov are
 * separate effects — only the flashbang routes through here.
 */

import { CONFIG } from '../config';
import type { Entity } from './world';

/** Start (or refresh) a full-duration blind on `e`. */
export function blindEntity(e: Entity): void {
  e.blindTimer = Math.max(e.blindTimer, CONFIG.blind.durationSec);
}

/** Age an entity's blind timer toward 0. Call once per tick. */
export function tickBlind(e: Entity, dt: number): void {
  if (e.blindTimer > 0) e.blindTimer = Math.max(0, e.blindTimer - dt);
}

/** True while blinded — whited-out vision (an NPC can't see the player). */
export function isBlinded(e: Entity): boolean {
  return e.blindTimer > 0;
}

/** True during the initial can't-move stun window at the start of a blind. */
export function isBlindStunned(e: Entity): boolean {
  return e.blindTimer > CONFIG.blind.durationSec - CONFIG.blind.stunSec;
}

/** Whiteout strength 0..1 — 1 at the flash, eroding linearly to 0 as it ends. */
export function blindIntensity(e: Entity): number {
  return Math.max(0, Math.min(1, e.blindTimer / CONFIG.blind.durationSec));
}

/** Minimal shape `blindBurst` needs — a live-check + tile position. */
interface Blindable extends Entity {
  body: { dead: boolean };
}

/**
 * Blind every live entity in `candidates` within `radius` Chebyshev tiles of
 * (x, y), except the one with id `exceptId` (the shooter isn't blinded by their
 * own muzzle). Used for an adjacent muzzle flash; the same call serves a future
 * flashbang with a larger radius.
 */
export function blindBurst(
  candidates: Iterable<Blindable>,
  x: number,
  y: number,
  radius: number,
  exceptId: number,
): number {
  let n = 0;
  for (const e of candidates) {
    if (e.id === exceptId || e.body.dead) continue;
    if (Math.max(Math.abs(e.x - x), Math.abs(e.y - y)) <= radius) {
      blindEntity(e);
      n++;
    }
  }
  return n;
}
